// TrendPeak_Analysis.tsx  (Hppc / Haryana)
import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getHaryanaDailyPeak,
  getHaryanaForecastDailyPeakDemand,
  getHaryanaMonthlyPeakDiscom,
  getHaryanaForecastMonthlyPeak,
} from "../../../Rest_api/RestApi";
import { H_CHART_TYPES } from "../const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";

// ─── Constants ────────────────────────────────────────────────────────────────

const X_AXIS = [
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

// All possible peak metric keys, tagged by their data type.
// These dataKey values must match the fields set on each processed row.
const ALL_PEAK_METRICS = [
  // Actual Load metrics
  { dataKey: "_peak_mw", displayName: "Day Peak", type: "demand" },
  { dataKey: "_morning_peak_mw", displayName: "Morning Peak", type: "demand" },
  { dataKey: "_evening_peak_mw", displayName: "Evening Peak", type: "demand" },
  // Forecast D+1 Load metrics
  {
    dataKey: "_forecasted_peak_mw",
    displayName: "Forecast D+1 Day Peak",
    type: "demand_forecast_d1",
  },
  {
    dataKey: "_forecasted_morning_peak_mw",
    displayName: "Forecast D+1 Morning Peak",
    type: "demand_forecast_d1",
  },
  {
    dataKey: "_forecasted_evening_peak_mw",
    displayName: "Forecast D+1 Evening Peak",
    type: "demand_forecast_d1",
  },
  // Forecast D+2 Load metrics
  {
    dataKey: "_forecasted_d2_peak_mw",
    displayName: "Forecast D+2 Day Peak",
    type: "demand_forecast_d2",
  },
  {
    dataKey: "_forecasted_d2_morning_peak_mw",
    displayName: "Forecast D+2 Morning Peak",
    type: "demand_forecast_d2",
  },
  {
    dataKey: "_forecasted_d2_evening_peak_mw",
    displayName: "Forecast D+2 Evening Peak",
    type: "demand_forecast_d2",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getMonthWithYear(month: number, year: number): string {
  if (month === undefined || year === undefined) return "";
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (month < 1 || month > 12) return "";
  return `${monthNames[month - 1]}-${year.toString().slice(-2)}`;
}

function parseMonthYear(monthYearStr: string | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) return { month: 0, year: 0 };
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parts = monthYearStr.split("-");
  if (parts.length !== 2) return { month: 0, year: 0 };
  const [monthStr, yearStr] = parts;
  return {
    month: monthNames.indexOf(monthStr) + 1,
    year: 2000 + parseInt(yearStr),
  };
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface HaryanaPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  /** selectedSuffix is the array of active suffix objects from the parent */
  selectedSuffix: Array<{
    suffix: string;
    displayName: string;
    stackSuffix: string;
  }>;
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: Array<{
    suffix: string;
    displayName: string;
    stackSuffix: string;
  }>;
  metrics: TrendData | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HaryanaPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  metrics,
}: HaryanaPeakAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);

  // Raw merged data — actual + forecast rows combined per day/month
  const [peakData, setPeakData] = useState<any[]>([]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const _handleFetchData = useCallback(async () => {
    setLoading(true);
    try {
      const trendDates = DefaultDates[activeTab]?.defaultTrendDates;
      if (!trendDates || trendDates.length === 0) {
        setPeakData([]);
        return;
      }

      // Collect all date values and find the overall min/max date range
      const allDates = trendDates.flatMap((dateRange: any) => {
        const [start, end] = dateRange.value.split(",");
        return [start, end].filter(Boolean);
      });

      if (allDates.length === 0) {
        setPeakData([]);
        return;
      }

      const sortedDates = [...allDates].sort(
        (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
      );
      const startDateStr = sortedDates[0];
      const endDateStr = sortedDates[sortedDates.length - 1];

      if (selectedXaxis.key === "month") {
        // ── Monthly: merge actual + forecast monthly peaks ─────────────────
        const [actualRaw, forecastRaw] = await Promise.all([
          getHaryanaMonthlyPeakDiscom(startDateStr, endDateStr),
          getHaryanaForecastMonthlyPeak(startDateStr, endDateStr),
        ]);

        // Index forecast rows by "month-year" for O(1) merge
        const forecastByMonthYear = new Map<string, any>(
          (Array.isArray(forecastRaw) ? forecastRaw : []).map((f: any) => [
            `${f.month}-${f.year}`,
            f,
          ]),
        );

        const processed = (Array.isArray(actualRaw) ? actualRaw : []).map(
          (item: any) => {
            const forecast =
              forecastByMonthYear.get(`${item.month}-${item.year}`) ?? {};
            return {
              ...item,
              month: getMonthWithYear(item.month, item.year),
              peak_mw: parseFloat(item.peak_mw?.toString() ?? "0"),
              morning_peak_mw: parseFloat(
                item.morning_peak_mw?.toString() ?? "0",
              ),
              evening_peak_mw: parseFloat(
                item.evening_peak_mw?.toString() ?? "0",
              ),
              average_mw: parseFloat(item.average_mw?.toString() ?? "0"),
              // D+1 forecast (monthly forecast API has one type)
              forecasted_peak_mw: parseFloat(
                forecast.peak_mw?.toString() ?? "0",
              ),
              forecasted_morning_peak_mw: parseFloat(
                forecast.morning_peak_mw?.toString() ?? "0",
              ),
              forecasted_evening_peak_mw: parseFloat(
                forecast.evening_peak_mw?.toString() ?? "0",
              ),
              // D+2 — monthly forecast API does not return separate D+2 data
              forecasted_d2_peak_mw: 0,
              forecasted_d2_morning_peak_mw: 0,
              forecasted_d2_evening_peak_mw: 0,
            };
          },
        );

        setPeakData(processed);
      } else {
        // ── Daily: fetch actual + forecast in parallel ─────────────────────
        // getDailyPeakForecast returns both T+1 and T+2 rows per date
        const [actualRaw, forecastRaw] = await Promise.all([
          getHaryanaDailyPeak(startDateStr, endDateStr),
          getHaryanaForecastDailyPeakDemand(startDateStr, endDateStr),
        ]);

        // Separate T+1 and T+2 forecast rows, index by date
        const forecastD1ByDate = new Map<string, any>(
          (Array.isArray(forecastRaw) ? forecastRaw : [])
            .filter((f: any) => f.forecast_type === "T+1")
            .map((f: any) => [f.date, f]),
        );
        const forecastD2ByDate = new Map<string, any>(
          (Array.isArray(forecastRaw) ? forecastRaw : [])
            .filter((f: any) => f.forecast_type === "T+2")
            .map((f: any) => [f.date, f]),
        );

        const processed = (Array.isArray(actualRaw) ? actualRaw : []).map(
          (item: any) => {
            const fcD1 = forecastD1ByDate.get(item.date) ?? {};
            const fcD2 = forecastD2ByDate.get(item.date) ?? {};
            return {
              date: item.date,
              peak_mw: parseFloat(item.peak_mw?.toString() ?? "0"),
              morning_peak_mw: parseFloat(
                item.morning_peak_mw?.toString() ?? "0",
              ),
              evening_peak_mw: parseFloat(
                item.evening_peak_mw?.toString() ?? "0",
              ),
              average_mw: parseFloat(item.average_mw?.toString() ?? "0"),
              morning_peak_slot: item.morning_peak_slot ?? "",
              evening_peak_slot: item.evening_peak_slot ?? "",
              // D+1 forecasted fields
              forecasted_peak_mw: parseFloat(fcD1.peak_mw?.toString() ?? "0"),
              forecasted_morning_peak_mw: parseFloat(
                fcD1.morning_peak_mw?.toString() ?? "0",
              ),
              forecasted_evening_peak_mw: parseFloat(
                fcD1.evening_peak_mw?.toString() ?? "0",
              ),
              // D+2 forecasted fields
              forecasted_d2_peak_mw: parseFloat(
                fcD2.peak_mw?.toString() ?? "0",
              ),
              forecasted_d2_morning_peak_mw: parseFloat(
                fcD2.morning_peak_mw?.toString() ?? "0",
              ),
              forecasted_d2_evening_peak_mw: parseFloat(
                fcD2.evening_peak_mw?.toString() ?? "0",
              ),
            };
          },
        );

        setPeakData(processed);
      }
    } catch (error) {
      console.error("Error fetching Haryana peak data:", error);
      setPeakData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key]);

  useEffect(() => {
    _handleFetchData();
  }, [_handleFetchData]);

  // ── Chart data derivation ─────────────────────────────────────────────────
  useEffect(() => {
    if (loading) return;

    if (peakData.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    // Sort chronologically
    const sorted = [...peakData].sort((a, b) => {
      if (selectedXaxis.key === "month") {
        const am = parseMonthYear(a.month);
        const bm = parseMonthYear(b.month);
        return am.year !== bm.year ? am.year - bm.year : am.month - bm.month;
      }
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    // Unit conversion factor
    const factor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    const converted = sorted.map((item) => ({
      ...item,
      peak_mw: (item.peak_mw ?? 0) * factor,
      morning_peak_mw: (item.morning_peak_mw ?? 0) * factor,
      evening_peak_mw: (item.evening_peak_mw ?? 0) * factor,
      average_mw: (item.average_mw ?? 0) * factor,
      forecasted_peak_mw: (item.forecasted_peak_mw ?? 0) * factor,
      forecasted_morning_peak_mw:
        (item.forecasted_morning_peak_mw ?? 0) * factor,
      forecasted_evening_peak_mw:
        (item.forecasted_evening_peak_mw ?? 0) * factor,
      forecasted_d2_peak_mw: (item.forecasted_d2_peak_mw ?? 0) * factor,
      forecasted_d2_morning_peak_mw:
        (item.forecasted_d2_morning_peak_mw ?? 0) * factor,
      forecasted_d2_evening_peak_mw:
        (item.forecasted_d2_evening_peak_mw ?? 0) * factor,
    }));

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month"],
      groupBy: [],
      volumeKey: "",
      aggregationKeys: [
        "peak_mw",
        "morning_peak_mw",
        "evening_peak_mw",
        "forecasted_peak_mw",
        "forecasted_morning_peak_mw",
        "forecasted_evening_peak_mw",
        "forecasted_d2_peak_mw",
        "forecasted_d2_morning_peak_mw",
        "forecasted_d2_evening_peak_mw",
      ],
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: converted,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [peakData, selectedXaxis, selectedUnit, loading]);

  // ── Active suffix keys derived from the Type filter ───────────────────────
  const activeSuffixKeys = useMemo(() => {
    return new Set(selectedSuffix.map((s) => s.suffix));
  }, [selectedSuffix]);

  // ── Legends — filtered by the active Type selection ───────────────────────
  const legends = useMemo(() => {
    return ALL_PEAK_METRICS.filter((metric) =>
      activeSuffixKeys.has(metric.type),
    ).map((metric) => ({
      dataKey: metric.dataKey,
      name: metric.displayName,
      legendColor: undefined,
      stroke: undefined,
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? metric.dataKey : undefined,
    }));
  }, [selectedChartType, activeSuffixKeys]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  // ── Summary helpers ───────────────────────────────────────────────────────
  const summaryExtraKeys = selectedXaxis.key === "date" ? ["date"] : ["month"];
  const summaryExtraLabels =
    selectedXaxis.key === "date" ? ["Date"] : ["Month"];
  const xDataKey = selectedXaxis.values[0];

  function getSelectedTypes() {
    return selectedSuffix.map((s) => s.displayName);
  }

  const commonSummaryProps = {
    legends: visibleLegends as any,
    data: chartData,
    tableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Haryana Load Trend Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(_, index) => setSelectedXaxis(X_AXIS[index])}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />

          {/* Type filter — reflects and updates the parent's selection */}
          <FilterMenu
            title="Type"
            items={chartSuffixes
              .filter((s) => s.suffix !== "deviation")
              .map((s) => s.displayName)}
            selectedItems={getSelectedTypes()}
            setSelectedItems={onTypeChange}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, index) => setSelectedChartType(H_CHART_TYPES[index])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-100">
                  Forecast Peak Analysis ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart") {
                        downloadChartTableCSV({ ...commonSummaryProps });
                      } else if (dataTab.label === "Summary") {
                        downloadSummaryCSV(commonSummaryProps);
                      } else if (dataTab.label === "Table") {
                        downloadChartTableCSV(commonSummaryProps);
                      }
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedGroupKeys}-${selectedUnit}-${selectedXaxis.key}-${selectedChartType.value}`}
                  data={chartData}
                  showBrush={true}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends as any}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  secondXDataKey={undefined}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable {...commonSummaryProps} />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip note={<p>HPPC</p>} text="Source">
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
